import { useState, type FC } from 'react';
import classNames from 'classnames';
import { CheckCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import BaseButton from 'components/atoms/baseButton';
import ButtonLoadingIcon from 'components/atoms/icons/buttonLoadingIcon';
import { useAppDispatch, useAppSelector } from 'store';
import {
  type TAddCommentFn,
  type TRpmDashboardActivityQueryKey,
  type TRpmPatientActivityQueryKey,
} from 'infrastructure/hooks/rpm';

import s from './styles.module.scss';

import type { IRpmPatientActivity } from 'infrastructure/interfaces/i-rpm-patient-activity';

interface ReviewedButtonProps {
  addComment: TAddCommentFn;
  idx: number | string;
  deviceId: string;
  readingId: string;
  currentInteractionStatus?: boolean;
  isReviewed: boolean;
  submitCommentLockByAi: boolean;
  disabled?: boolean;
  getActivityQueryKey:
    | TRpmDashboardActivityQueryKey
    | TRpmPatientActivityQueryKey;
}

const ReviewedButton: FC<ReviewedButtonProps> = (props) => {
  const {
    addComment,
    idx,
    deviceId,
    readingId,
    currentInteractionStatus,
    isReviewed,
    submitCommentLockByAi,
    disabled: propsDisabled,
    getActivityQueryKey,
  } = props;

  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [reviewed, setReviewed] = useState(false);

  const actions = useAppSelector((state) => state.activityActions.actions[idx]);
  const {
    activityActions: { updateAction, resetCurrentActions },
  } = useAppDispatch();

  const disabled =
    propsDisabled ||
    loading ||
    actions?.sending ||
    reviewed ||
    isReviewed ||
    submitCommentLockByAi;

  const handleMarkReviewed = async () => {
    setLoading(true);
    updateAction({ idx, action: { sending: true } });

    if (actions?.message) {
      await addComment(
        {
          deviceId,
          readingId,
          message: actions.message,
          currentInteractionStatus,
        },
        {
          async onSuccess() {
            resetCurrentActions(idx);
            await addComment(
              {
                deviceId,
                readingId,
                reviewed: true,
                currentInteractionStatus,
              },
              {
                onSuccess() {
                  setReviewed(true);
                  queryClient.invalidateQueries<IRpmPatientActivity>(
                    getActivityQueryKey,
                  );
                },
                onSettled() {
                  setLoading(false);
                },
              },
            );
          },
          onError() {
            updateAction({ idx, action: { sending: false } });
            setLoading(false);
            setReviewed(false);
          },
        },
      );
    } else {
      await addComment(
        {
          deviceId,
          readingId,
          reviewed: true,
          currentInteractionStatus,
        },
        {
          onSuccess() {
            setReviewed(true);
            queryClient.invalidateQueries<IRpmPatientActivity>(
              getActivityQueryKey,
            );
          },
          onSettled() {
            setLoading(false);
            resetCurrentActions(idx);
          },
        },
      );
    }
  };

  if (loading) {
    return (
      <BaseButton
        type="secondary"
        className={classNames(s['action-button'], s['mark-reviewed-button'])}
        label={t('labels.markReviewed')}
        icon={<ButtonLoadingIcon className={s['spin-loading-icon']} />}
        disabled
      />
    );
  }

  if (reviewed || isReviewed) {
    return (
      <BaseButton
        type="secondary"
        className={classNames(s['action-button'], s['mark-reviewed-button'])}
        label={t('labels.reviewed')}
        icon={<CheckCircleOutlined />}
        disabled
      />
    );
  }

  return (
    <BaseButton
      type="secondary"
      className={classNames(s['action-button'], s['mark-reviewed-button'])}
      label={t('labels.markReviewed')}
      icon={<CheckCircleOutlined />}
      onClick={handleMarkReviewed}
      disabled={disabled}
    />
  );
};

export default ReviewedButton;
